import { BitkitControlButton, IconChevronRight, IconThumbDown, IconThumbUp } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Card } from '@chakra-ui/react/card';
import { Text } from '@chakra-ui/react/text';
import { keyframes } from '@emotion/react';
import { ReactNode, useState } from 'react';

import MarkdownBlock from '@/components/MarkdownBlock';

export const AIMessage = ({ userMessage, children }: { userMessage: string; children: ReactNode }) => {
  return (
    <div>
      <Box display="flex" justifyContent="flex-end" mb="16">
        <Card.Root asChild width="fit-content" elevation={false} paddingSize="sm">
          <Card.Body textStyle="body/md/regular">{userMessage}</Card.Body>
        </Card.Root>
      </Box>
      {children}
    </div>
  );
};

const AIFeedback = ({ onFeedback }: { onFeedback: (reactionType: 'upvote' | 'downvote') => void }) => {
  const [feedbackSent, setFeedbackSent] = useState(false);

  const handleFeedback = (reactionType: 'upvote' | 'downvote') => {
    onFeedback(reactionType);
    setFeedbackSent(true);
  };

  return (
    <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
      {feedbackSent ? (
        <Text textStyle="body/md/semibold">Thank you for your feedback!</Text>
      ) : (
        <Box display="flex" flexDirection="row" alignItems="center">
          <BitkitControlButton
            icon={IconThumbUp}
            color="icon/tertiary"
            label="Useful"
            onClick={() => handleFeedback('upvote')}
          />
          <BitkitControlButton
            color="icon/tertiary"
            icon={IconThumbDown}
            label="Not useful"
            onClick={() => handleFeedback('downvote')}
          />
        </Box>
      )}
    </Box>
  );
};

export const AILoadingText = () => {
  const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

  return (
    <Text
      color="text/secondary"
      textStyle="body/md/regular"
      verticalAlign="bottom"
      bgImage="linear-gradient(90deg, rgba(0, 0, 0, 0) calc(50% - 60px), color-mix(in srgb, var(--colors-background\/primary) 84%, transparent), rgba(0, 0, 0, 0) calc(50% + 60px)), linear-gradient(var(--colors-text\/secondary), var(--colors-text\/secondary))"
      bgSize="200% auto"
      bgClip="text"
      animation={`${shimmer} 3s linear infinite`}
    >
      Working...
    </Text>
  );
};

export const AILoading = ({ log }: { log?: string }) => {
  return (
    <div>
      <AILoadingText />
      {log && (
        <Text marginTop="8" whiteSpace="preserve wrap" textStyle="body/md/regular" color="text/secondary">
          {log}
        </Text>
      )}
    </div>
  );
};

type AIHistoryItemProps = {
  status: 'successful' | 'failed';
  durationSeconds?: number;
  response?: string;
  onViewLog?: () => void;
  onFeedback?: (reactionType: 'upvote' | 'downvote') => void;
  children?: ReactNode;
};

export const AIHistoryItem = ({
  status,
  durationSeconds,
  response,
  onViewLog,
  onFeedback,
  children,
}: AIHistoryItemProps) => {
  return (
    <Box display="flex" flexDirection="column" gap="12">
      {onViewLog && (
        <Box
          as="button"
          textStyle="body/md/regular"
          onClick={onViewLog}
          className="ai-history-log"
          display="flex"
          flexDirection="row"
          whiteSpace="pre"
          color="text/tertiary"
          alignItems="center"
        >
          {status === 'successful' ? (
            <>
              <Text as="span" textStyle="body/md/semibold" color="text/secondary">
                Worked
              </Text>
              {durationSeconds !== undefined && <> for {durationSeconds} seconds</>}
            </>
          ) : (
            'Failed'
          )}
          <IconChevronRight
            color="icon/tertiary"
            marginLeft="8"
            size="16"
            display="none"
            css={{ '.ai-history-log:hover &': { display: 'block' } }}
          />
        </Box>
      )}
      {status === 'successful' && response && (
        <>
          <MarkdownBlock size="md" md={response} />
          {children}
          {onFeedback && <AIFeedback onFeedback={onFeedback} />}
        </>
      )}
    </Box>
  );
};
